/**
 * Home Analytics: cohorte Creado at → estado HOY.
 * @module-isolated analytics-cohort
 * @depends api.js, portal-permissions (solo lectura vía AuthGate)
 * @consumers ninguno — solo /
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DateRangeFilter,
  ErrorState,
  FilterBar,
  KpiCard,
  PageHeader
} from '../../../shared/components/index.js';
import OriginCampaignFilter from '../components/OriginCampaignFilter.jsx';
import StateDistribution from '../components/StateDistribution.jsx';
import StatusByCampaignChart from '../components/StatusByCampaignChart.jsx';
import StatePies from '../components/StatePies.jsx';
import CohortLeadTable from '../components/CohortLeadTable.jsx';
import { useCohortData } from '../hooks/useCohortData.js';
import {
  countByMeetingStatus,
  distributeByKommoStage,
  distributeByState,
  filterByMeeting,
  filterCohort,
  leadsInKommoStage,
  leadsInState,
  listCampaigns,
  originHint,
  uniqueByLeadId
} from '../utils/filter-cohort.js';
import { MEETING_STATUSES, meetingStatusLabel } from '../../../shared/constants/meeting-statuses.js';

function formatYmdCl(isoOrYmd) {
  const ymd = String(isoOrYmd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return String(isoOrYmd || '');
  return ymd.slice(8, 10) + '-' + ymd.slice(5, 7) + '-' + ymd.slice(0, 4);
}

function createdSpan(leads) {
  let min = '';
  let max = '';
  (leads || []).forEach((l) => {
    const ymd = String((l && l.createdAtIso) || '').slice(0, 10);
    if (!ymd) return;
    if (!min || ymd < min) min = ymd;
    if (!max || ymd > max) max = ymd;
  });
  return { min: min, max: max };
}

function formatSyncAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

export default function CohortHomePage({ email }) {
  void email;
  const {
    period,
    setPeriod,
    periodOptions,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    periodLabel,
    loading,
    awaitingCustomDates,
    error,
    data,
    range,
    syncBusy,
    reload,
    cancelSearch,
    runSync,
    originIds,
    setOriginIds,
    campaignIds,
    setCampaignIds,
    allMetaCampaigns,
    setAllMetaCampaigns,
    nonMetaOriginOptions,
    metaAdsCampaigns
  } = useCohortData();

  const [selectedState, setSelectedState] = useState('');
  const [meetingFilter, setMeetingFilter] = useState('');
  const [stateTab, setStateTab] = useState('gi');

  const rawLeads = useMemo(
    () => uniqueByLeadId((data && data.cohortLeads) || []),
    [data]
  );
  const campaigns = useMemo(() => {
    const fromAds = (metaAdsCampaigns || [])
      .map((row) => ({
        id: row.id,
        label: row.name,
        count: rawLeads.filter((lead) => String(lead.metaCampaignId || '') === row.id).length
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'));
    if (fromAds.length) return fromAds;
    return listCampaigns(rawLeads);
  }, [metaAdsCampaigns, rawLeads]);
  const universe = useMemo(
    () =>
      filterCohort(rawLeads, {
        originIds: originIds,
        campaignIds: campaignIds,
        allMetaCampaigns: allMetaCampaigns
      }),
    [rawLeads, originIds, campaignIds, allMetaCampaigns]
  );
  const distGi = useMemo(
    () => distributeByState(universe, (data && data.internalStates) || []),
    [universe, data]
  );
  const distKommo = useMemo(() => distributeByKommoStage(universe), [universe]);
  const dist = stateTab === 'kommo' ? distKommo : distGi;
  const meetingCounts = useMemo(() => countByMeetingStatus(universe), [universe]);
  const markedStages =
    data && data.meetingMapping ? Number(data.meetingMapping.markedStages || 0) : 0;
  const detailLeads = useMemo(() => {
    const inBucket =
      stateTab === 'kommo'
        ? leadsInKommoStage(universe, selectedState)
        : leadsInState(universe, selectedState);
    return filterByMeeting(inBucket, meetingFilter);
  }, [universe, selectedState, meetingFilter, stateTab]);
  const meetingFilterLabel = meetingStatusLabel(meetingFilter);
  const selectedName = useMemo(() => {
    if (!selectedState) return '';
    const row = dist.rows.find((r) => r.id === selectedState);
    return row ? row.name : '';
  }, [dist.rows, selectedState]);

  const hint = originHint(
    { originIds: originIds, campaignIds: campaignIds, allMetaCampaigns: allMetaCampaigns },
    campaigns.length
  );
  const filterFrom =
    (data && data.filters && data.filters.dateFrom) || (range && range.dateFrom) || '';
  const filterTo =
    (data && data.filters && data.filters.dateTo) || (range && range.dateTo) || '';
  const span = useMemo(() => createdSpan(universe), [universe]);
  const sync = data && data.kommoSync;
  const syncLabel = sync && sync.lastSyncAt
    ? 'Última sincronización Kommo: ' + formatSyncAt(sync.lastSyncAt)
    : 'Última sincronización Kommo: no registrada';

  return (
    <div
      className={
        'analytics-module analytics-cohort-page' +
        (loading || syncBusy ? ' is-loading-period' : '')
      }
    >
      <PageHeader
        eyebrow="Analytics"
        title="Leads"
        subtitle={
          'Creados en Kommo en el período · estado actual hoy. ' +
          (filterFrom && filterTo
            ? formatYmdCl(filterFrom) + ' → ' + formatYmdCl(filterTo) + '.'
            : periodLabel + '.')
        }
        actions={
          <FilterBar label="Filtros de cohorte">
            <DateRangeFilter
              id="cohort-period"
              value={period}
              options={periodOptions}
              disabled={syncBusy}
              onChange={setPeriod}
            />
            {period === 'custom' ? (
              <>
                <label className="analytics-field">
                  <span>Desde</span>
                  <input
                    type="date"
                    value={dateFrom}
                    disabled={syncBusy}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </label>
                <label className="analytics-field">
                  <span>Hasta</span>
                  <input
                    type="date"
                    value={dateTo}
                    disabled={syncBusy}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </label>
              </>
            ) : null}
            <OriginCampaignFilter
              originOptions={nonMetaOriginOptions}
              originIds={originIds}
              onOriginChange={setOriginIds}
              campaigns={campaigns}
              campaignIds={campaignIds}
              onCampaignChange={setCampaignIds}
              allMetaCampaigns={allMetaCampaigns}
              onAllMetaCampaignsChange={setAllMetaCampaigns}
              disabled={syncBusy}
              loading={loading && !data}
            />
            <button
              type="button"
              className={'analytics-btn' + (loading ? ' is-loading' : '')}
              disabled={syncBusy || awaitingCustomDates}
              aria-busy={loading ? 'true' : 'false'}
              title="Consultar el período elegido"
              onClick={() => reload()}
            >
              {loading ? 'Buscando…' : 'Buscar'}
            </button>
            {loading ? (
              <button
                type="button"
                className="analytics-btn analytics-btn-ghost"
                title="Interrumpir la búsqueda en curso"
                onClick={() => cancelSearch()}
              >
                Cancelar
              </button>
            ) : null}
            <button
              type="button"
              className="analytics-btn analytics-btn-ghost"
              disabled={syncBusy}
              title="Sincroniza el espejo Kommo"
              onClick={runSync}
            >
              {syncBusy ? 'Sincronizando…' : 'Sincronizar Kommo'}
            </button>
          </FilterBar>
        }
      />

      <p className="analytics-meta-bar">{syncLabel}</p>
      {data && filterFrom && filterTo ? (
        <p className="analytics-cohort-note">
          Filtro <strong>Creado at</strong> Kommo: {formatYmdCl(filterFrom)} →{' '}
          {formatYmdCl(filterTo)}
          {span.min
            ? ' · en esta lista el más antiguo es ' +
              formatYmdCl(span.min) +
              ' y el más nuevo ' +
              formatYmdCl(span.max)
            : ''}
          . No es el stock de todos los leads abiertos.
        </p>
      ) : null}

      {awaitingCustomDates ? (
        <p className="analytics-cohort-note">Elegí Desde y Hasta para armar la cohorte.</p>
      ) : null}

      {!data && !loading && !syncBusy && !error && !awaitingCustomDates ? (
        <p className="analytics-empty">
          Elegí el período y pulsá <strong>Buscar</strong>. No se consulta hasta entonces.
        </p>
      ) : null}

      {loading || syncBusy ? (
        <div className="analytics-leads-loading" role="status" aria-live="polite">
          <span className="analytics-leads-loading-dot" aria-hidden />
          {syncBusy
            ? 'Sincronizando Kommo…'
            : 'Buscando leads… usá Cancelar arriba si querés interrumpir.'}
        </div>
      ) : null}
      <ErrorState message={error} onRetry={() => reload()} />

      {data ? (
        <>
          <div className="analytics-cohort-kpis">
            <KpiCard
              label="Leads"
              value={Number(dist.total).toLocaleString('es-CL')}
              hint={
                (filterFrom && filterTo
                  ? 'Creado at ' + formatYmdCl(filterFrom) + ' → ' + formatYmdCl(filterTo)
                  : 'Creados en el período seleccionado') + (hint ? ' · ' + hint : '')
              }
            />
            {MEETING_STATUSES.map((s) => (
              <KpiCard
                key={s.id}
                label={s.homeLabel}
                value={Number(meetingCounts[s.id] || 0).toLocaleString('es-CL')}
                hint={
                  markedStages === 0
                    ? 'Sin mapeo: definí etapas en Estados de reunión'
                    : 'Etapa Kommo actual · clic para filtrar'
                }
                tone={markedStages === 0 ? 'warn' : meetingFilter === s.id ? 'ok' : ''}
                selected={meetingFilter === s.id}
                onClick={() =>
                  setMeetingFilter((prev) => (prev === s.id ? '' : s.id))
                }
              />
            ))}
          </div>

          {markedStages === 0 ? (
            <p className="analytics-cohort-warn" role="status">
              Los indicadores de reunión quedan en 0 hasta que mapees etapas en{' '}
              <Link to="/configuracion/reunion">Estados de reunión</Link>. Es el estado
              Kommo de hoy, no el historial de pasos.
            </p>
          ) : (
            <p className="analytics-cohort-note">
              Cada lead entra en un solo estado de reunión, según la etapa en la que está
              hoy.{' '}
              <Link to="/configuracion/reunion">Editar mapeo</Link>
              {meetingFilterLabel ? ' · tabla filtrada: ' + meetingFilterLabel + '.' : ''}
            </p>
          )}

          <StatePies giRows={distGi.rows} kommoRows={distKommo.rows} />

          <div className="leads-subnav config-tabs" style={{ marginBottom: 4 }}>
            <button
              type="button"
              className={'leads-subnav-link' + (stateTab === 'gi' ? ' is-active' : '')}
              onClick={() => {
                setStateTab('gi');
                setSelectedState('');
              }}
            >
              Estados GI
            </button>
            <button
              type="button"
              className={'leads-subnav-link' + (stateTab === 'kommo' ? ' is-active' : '')}
              onClick={() => {
                setStateTab('kommo');
                setSelectedState('');
              }}
            >
              Estados Kommo
            </button>
          </div>

          <StatusByCampaignChart
            leads={universe}
            sourceLeads={rawLeads}
            namedCampaigns={metaAdsCampaigns}
            stateRows={dist.rows}
            mode={stateTab}
          />

          <StateDistribution
            rows={dist.rows}
            total={dist.total}
            selectedId={selectedState}
            onSelect={setSelectedState}
            title="Estado actual"
            note={
              stateTab === 'kommo'
                ? 'Etapa real del embudo Kommo HOY · sin el mapeo de Configuración'
                : 'Dónde están HOY estos leads, según el mapeo GI · no es cómo estaban al cierre del período'
            }
          />

          <CohortLeadTable
            leads={detailLeads}
            stateName={selectedName}
            meetingFilterLabel={meetingFilterLabel}
          />
        </>
      ) : null}
    </div>
  );
}
